import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import LegalLayout, { H2, H3, P, UL, Note } from "./LegalLayout";
import api from "@/lib/api";
import { renderMarkdown } from "@/lib/landing";

const TOC = [
  { id: "acceptance",      label: "1. Acceptance of Terms" },
  { id: "service",         label: "2. The Service" },
  { id: "accounts",        label: "3. Account Registration & Approval" },
  { id: "your-content",    label: "4. Your Content" },
  { id: "acceptable-use",  label: "5. Acceptable Use" },
  { id: "billing",         label: "6. Subscription & Billing" },
  { id: "suspension",      label: "7. Suspension & Termination" },
  { id: "ip",              label: "8. Intellectual Property" },
  { id: "warranties",      label: "9. Disclaimers" },
  { id: "liability",       label: "10. Limitation of Liability" },
  { id: "indemnity",       label: "11. Indemnification" },
  { id: "governing-law",   label: "12. Governing Law" },
  { id: "changes",         label: "13. Changes to these Terms" },
  { id: "contact",         label: "14. Contact" },
];

export default function TermsOfUsePage() {
  const [override, setOverride] = useState(null);
  useEffect(() => {
    api.get("/site-content").then(({ data }) => {
      if (data?.terms_of_use_md && data.terms_of_use_md.trim()) setOverride(data.terms_of_use_md);
    }).catch(() => {});
  }, []);
  return (
    <LegalLayout
      title="Terms of Use"
      subtitle="The rules that govern your use of SchoolBoss. Please read carefully — creating an account or accessing a school portal means you accept these terms."
      updatedAt="February 2026"
      toc={override ? null : TOC}
    >
      {override ? (
        <div className="prose prose-slate max-w-none [&_h1]:font-heading [&_h1]:text-3xl [&_h1]:font-bold [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-bold [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:space-y-1.5 [&_a]:text-[#2563EB] [&_a]:font-semibold hover:[&_a]:underline" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(override), { USE_PROFILES: { html: true } }) }} />
      ) : (<>
      <H2 id="acceptance">1. Acceptance of Terms</H2>
      <P>
        By accessing or using SchoolBoss.Ph ("the Service"), you agree to be bound by these Terms of Use ("Terms"). If you are using the Service on behalf of a school, you represent that you have authority to bind that school to these Terms. If you do not agree, do not use the Service.
      </P>

      <H2 id="service">2. The Service</H2>
      <P>
        SchoolBoss provides a multi-tenant cloud platform for managing schools and their branches, including modules for enrollment, attendance, finance, communications and reporting. The Service is provided "as-is" and may evolve over time. New features may be added, and existing features may be modified or removed with reasonable notice.
      </P>

      <H2 id="accounts">3. Account Registration &amp; Approval</H2>
      <UL>
        <li><strong>School owners</strong> create an account by registering their school. Registrations are reviewed by SchoolBoss before going live. We reserve the right to refuse registration without obligation to provide a reason.</li>
        <li><strong>Branches</strong> added by an owner are also subject to review before they go live.</li>
        <li><strong>Staff accounts</strong> are created by the school owner. Staff sign in through the school's branded portal.</li>
        <li><strong>Parent accounts</strong> are created automatically the first time a guardian signs in with their email and child's birthday. Parents must set a new password on first login.</li>
      </UL>
      <Note>
        You are responsible for keeping your password confidential. Notify us immediately at <span className="font-mono text-[13px]">support@schoolboss.ph</span> if you suspect unauthorized access to your account.
      </Note>

      <H2 id="your-content">4. Your Content</H2>
      <P>
        Schools retain full ownership of the data they upload to SchoolBoss — including but not limited to student records, attendance, financial entries, grades, photos and feedback notes ("Your Content").
      </P>
      <P>
        By uploading Your Content, you grant SchoolBoss a worldwide, royalty-free license to host, store, process and display Your Content solely for the purpose of operating and improving the Service. This license terminates when you delete the content from the Service.
      </P>
      <P>
        You are solely responsible for ensuring you have the right to upload Your Content and that any personal information you upload was collected with appropriate consent.
      </P>

      <H2 id="acceptable-use">5. Acceptable Use</H2>
      <P>You agree NOT to:</P>
      <UL>
        <li>Upload illegal, defamatory, or infringing content</li>
        <li>Use the Service to spam, harass, or target minors maliciously</li>
        <li>Attempt to gain unauthorized access to other schools' data</li>
        <li>Reverse-engineer, decompile, or attempt to extract the source code of the Service</li>
        <li>Use the Service to operate competing services or scrape data systematically</li>
        <li>Upload viruses, worms, or any malicious code</li>
        <li>Resell, sublicense, or otherwise transfer your account to a third party without prior written consent</li>
      </UL>

      <H2 id="billing">6. Subscription &amp; Billing</H2>
      <H3>6.1 Plans</H3>
      <P>
        SchoolBoss offers a free Starter tier (1 branch, up to 50 students), a paid Growth tier, and a custom Scale tier. Pricing is listed at <a href="/#pricing" className="text-[#2563EB] font-semibold hover:underline">/#pricing</a> and may change with 30 days notice.
      </P>
      <H3>6.2 Payment</H3>
      <P>
        Paid subscriptions are billed monthly in advance. Fees are non-refundable except where required by law. Failure to pay may result in suspension of the Service.
      </P>
      <H3>6.3 Taxes</H3>
      <P>Fees are exclusive of applicable taxes. You are responsible for any taxes imposed on your subscription.</P>

      <H2 id="suspension">7. Suspension &amp; Termination</H2>
      <P>
        We may suspend or terminate your access to the Service if you breach these Terms, if your account is overdue, or if continued use poses a security or legal risk. We will give you reasonable notice except where immediate action is necessary to protect the platform.
      </P>
      <P>
        You may terminate your subscription at any time from your account settings. Upon termination, Your Content remains accessible for 90 days, after which it is permanently deleted from active systems (see our <a href="/privacy#retention" className="text-[#2563EB] font-semibold hover:underline">Privacy Policy</a>).
      </P>

      <H2 id="ip">8. Intellectual Property</H2>
      <P>
        The SchoolBoss brand, logo, source code, design, documentation, and the structure of the Service are owned by SchoolBoss.Ph and are protected by intellectual-property laws. Nothing in these Terms transfers ownership of the Service to you. You may use the Service only within the scope of these Terms.
      </P>

      <H2 id="warranties">9. Disclaimers</H2>
      <P>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
      </P>
      <P>
        We do not warrant that the Service will be uninterrupted, error-free, or that data will be perfectly accurate. We do, however, work hard to make it close to that — and we will be transparent when issues arise.
      </P>

      <H2 id="liability">10. Limitation of Liability</H2>
      <P>
        To the maximum extent permitted by law, SchoolBoss shall not be liable for any indirect, incidental, special, consequential or punitive damages, including but not limited to loss of revenue, data, goodwill or other intangible losses, even if we have been advised of the possibility of such damages.
      </P>
      <P>
        Our total aggregate liability arising out of or relating to these Terms or the Service shall not exceed the total fees you paid us in the 12 months immediately preceding the event giving rise to the liability.
      </P>

      <H2 id="indemnity">11. Indemnification</H2>
      <P>
        You agree to defend, indemnify, and hold harmless SchoolBoss.Ph, its officers, directors, employees and agents from any claim arising out of or related to: (a) Your Content, (b) your use of the Service, or (c) your breach of these Terms.
      </P>

      <H2 id="governing-law">12. Governing Law</H2>
      <P>
        These Terms are governed by the laws of the Republic of the Philippines. Any dispute arising out of or relating to these Terms shall be brought exclusively in the courts of Dasmariñas, Cavite, Philippines.
      </P>

      <H2 id="changes">13. Changes to these Terms</H2>
      <P>
        We may update these Terms from time to time. When we do, we will update the "Last updated" date at the top. Material changes will be announced in-app at least 14 days before they take effect. Continued use of the Service after a change takes effect constitutes acceptance of the revised Terms.
      </P>

      <H2 id="contact">14. Contact</H2>
      <P>
        SchoolBoss.Ph<br />
        Governors Drive, Sampaloc 1, Dasmariñas, Philippines, 4114<br />
        Email: <span className="font-mono text-[13px]">legal@schoolboss.ph</span><br />
        Phone: +63 908 908 4776
      </P>
      </>)}
    </LegalLayout>
  );
}
